/**
 * Onboarding State Components
 *
 * Loading and Error state components for the Onboarding compound.
 */

import * as React from 'react';
import { AlertCircleIcon, IconSizes, XCloseIcon } from '@newcore/icons';
import { cn } from '@newcore/ui/cn';
import { AlertVariants } from '@newcore/ui/enums/variants';
import { Spinner } from '@newcore/ui/loading';
import { Text } from '@newcore/ui/typography';
import { Alert } from '@/components/ui/alert';
import type { OnboardingErrorProps, OnboardingLoadingProps } from './types';

// =============================================================================
// Loading State
// =============================================================================

/**
 * Loading spinner with optional message
 *
 * @example
 * <Onboarding.Loading message="Setting up your account..." />
 */
export const OnboardingLoading: React.FC<OnboardingLoadingProps> = ({
  message = 'Loading...',
  className,
}): React.ReactElement => {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center py-12',
        className
      )}
    >
      <Spinner size={IconSizes.LG} className='text-primary' />
      <Text as='p' className='mt-4 text-tertiary'>
        {message}
      </Text>
    </div>
  );
};

// =============================================================================
// Error State
// =============================================================================

/**
 * Error alert with optional dismiss button
 *
 * @example
 * <Onboarding.Error error={error} onDismiss={clearError} />
 */
export const OnboardingError: React.FC<OnboardingErrorProps> = ({
  error,
  onDismiss,
  className,
}): React.ReactElement => {
  return (
    <Alert
      variant={AlertVariants.DESTRUCTIVE}
      className={cn('relative', className)}
    >
      <AlertCircleIcon className='h-4 w-4' />
      <Text as='div' className='ml-2 flex-1'>
        {error}
      </Text>
      {onDismiss && (
        <button
          type='button'
          onClick={onDismiss}
          className={cn(
            'absolute right-2 top-2 rounded-sm p-1',
            'opacity-70 transition-opacity hover:opacity-100',
            'focus:outline-none focus:ring-2 focus:ring-focus-ring'
          )}
          aria-label='Dismiss error'
        >
          <XCloseIcon className='h-3 w-3' />
        </button>
      )}
    </Alert>
  );
};
